import { CameraControls } from '@react-three/drei';
import debounce from 'lodash.debounce';
import { Object3D } from 'three';
import { Duration } from 'types';
import { proxy, ref } from 'valtio';
import { devtools } from 'valtio/utils';

import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import postMotionSettings from '@cartwhl/common/api/postMotionSettings';
import { setStoreValues } from '@cartwhl/common/utils';

import cameraControlsFollow from 'components/AnimationPlayer/utils';
import useCanvasRecorder from 'hooks/useCanvasRecorder';
import { ProgressInfo } from 'hooks/useFfmpeg';
import {
  animationGenerationStore,
  authStore,
  sceneSettingsStore,
} from 'stores';
import { sleep } from 'utils/sleep';

import { USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME } from './sceneSettingsStore';

type AnimationPlayerStoreState = {
  autoPlayOnLoad: boolean;
  controls: 'blank' | 'player' | 'trim';
  inputMode: 'rotate' | 'truck' | 'dolly';
  currentFrame: number;
  currentTime: number;
  isPlaying: boolean;
  isRecording: boolean;
  duration: Duration;
  mixerReady: boolean;
  recordedVideo?: Blob;
  trimFrameStart?: number;
  trimFrameEnd?: number;
  enableRootMotion: [boolean, boolean, boolean];
  videoGenerationProgress?: ProgressInfo;
  disabled?: boolean;
};

const defaultValues: AnimationPlayerStoreState = {
  autoPlayOnLoad: true,
  controls: 'player',
  inputMode: 'rotate',
  isPlaying: false,
  isRecording: false,
  currentFrame: 1,
  currentTime: 0,
  duration: {
    originalSeconds: 0,
    originalFrames: 0,
    seconds: 0,
    frames: 0,
  },
  mixerReady: false,
  recordedVideo: undefined,
  trimFrameStart: undefined,
  trimFrameEnd: undefined,
  enableRootMotion: [true, true, true] as [boolean, boolean, boolean],
  videoGenerationProgress: undefined,
  disabled: false,
};

export class AnimationPlayerStoreInstance {
  public static instances = new Map<string, AnimationPlayerStoreInstance>();
  public cameraControls?: CameraControls;
  public animationMixer?: ReturnType<typeof useFrameMixer>;
  public canvasRecorder?: ReturnType<typeof useCanvasRecorder>;
  public state: AnimationPlayerStoreState;
  private recordingAbortController?: AbortController;

  constructor() {
    this.state = proxy<AnimationPlayerStoreState>({ ...defaultValues });

    if (process.env.NODE_ENV === 'development') {
      devtools(this.state, { name: 'AnimationPlayer', enabled: true });
    }
  }

  frameJump = (numFrames: number) => {
    const currFrame = this.animationMixer?.getFrame();
    if (currFrame !== undefined) {
      const { frameStepSize } = sceneSettingsStore.state.playbackSettings;
      const nextFrame = currFrame + numFrames * frameStepSize;
      this.animationMixer?.setFrame(nextFrame);
    }
  };

  setFrame = (frame: number) => {
    this.animationMixer?.setFrame(frame);
  };

  playOrPause = (playState?: boolean) => {
    if (playState !== undefined) {
      this.animationMixer?.play(playState);
    } else {
      this.animationMixer?.play(!this.state.isPlaying);
    }
    this.frameJump(0);
  };

  setIsPlaying = (value: boolean) => {
    this.state.isPlaying = value;
  };

  setCameraControl = (controls: CameraControls) => {
    this.cameraControls = controls;
    this.setCameraInputMode(this.state.inputMode);
  };

  hoistAnimationMixer = (controls: ReturnType<typeof useFrameMixer>) => {
    this.animationMixer = controls;
  };

  setCurrentFrame = (currentFrame: number) => {
    this.state.currentFrame = currentFrame;
    this.state.currentTime =
      (currentFrame * 1) / sceneSettingsStore.state.playbackSettings.frameRate;
  };

  setDuration = (duration: Duration) => {
    if (
      this.state.duration.originalSeconds !== duration.originalSeconds ||
      this.state.duration.originalFrames !== duration.originalFrames ||
      this.state.duration.seconds !== duration.seconds ||
      this.state.duration.frames !== duration.frames
    ) {
      this.state.duration = duration;
    }
  };

  toggleRootMotion = () => {
    const current = this.state.enableRootMotion ?? [true, true, true];
    const [x, y, z] = current;
    const horizontalOn = x && y;
    const next: [boolean, boolean, boolean] = horizontalOn
      ? [false, false, z]
      : [true, true, z];

    this.state.enableRootMotion = next;
  };

  zoomIn = () => {
    this.cameraControls?.dolly(1, true);
  };

  zoomOut = () => {
    this.cameraControls?.dolly(-1, true);
  };

  resetCamera = () => {
    return this.cameraControls?.reset(true);
  };

  setControls = (mode: AnimationPlayerStoreState['controls']) => {
    this.state.controls = mode;
  };

  setMixerReady = (ready: boolean) => {
    this.state.mixerReady = ready;
  };

  updateCamera = ({
    cameraFollow,
    delta,
    serializedCamera,
    target,
  }: {
    cameraFollow: boolean;
    delta: number;
    serializedCamera?: string;
    target?: Object3D;
  }) => {
    if (
      !target ||
      !this.cameraControls ||
      !cameraFollow ||
      !(this.state.isPlaying || this.state.isRecording)
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const isThumbnail =
      params.get(USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME) === 'true';

    cameraControlsFollow({
      delta,
      target,
      cameraControls: this.cameraControls,
      isThumbnail,
      serializedCamera,
    });
  };

  setTrimFrames = (frames?: [number, number]) => {
    if (frames) {
      this.state.trimFrameStart = Math.round(frames[0]);
      this.state.trimFrameEnd = Math.round(frames[1]);
      if (this === defaultInstance) {
        this.saveTrimSettings();
      }
    }
  };

  setCameraInputMode = (mode: 'rotate' | 'truck' | 'dolly') => {
    if (!this.cameraControls) return;
    this.state.inputMode = mode;
    if (mode === 'truck') {
      this.cameraControls.mouseButtons.left = 2;
      this.cameraControls.mouseButtons.right = 1;
      this.cameraControls.mouseButtons.middle = 8;
    } else if (mode === 'rotate') {
      this.cameraControls.mouseButtons.left = 1;
      this.cameraControls.mouseButtons.right = 2;
      this.cameraControls.mouseButtons.middle = 8;
    } else {
      this.cameraControls.mouseButtons.left = 8;
      this.cameraControls.mouseButtons.right = 2;
      this.cameraControls.mouseButtons.middle = 1;
    }
  };

  setCanvasRecorder = (recorder: ReturnType<typeof useCanvasRecorder>) => {
    this.canvasRecorder = recorder;
  };

  startVideoRecording = async (
    onProgress?: (progress: ProgressInfo) => void,
  ) => {
    await sleep(100);
    this.animationMixer?.play(false);
    this.state.isRecording = true;
    this.state.videoGenerationProgress = undefined;

    // Create abort controller for this recording session
    this.recordingAbortController = new AbortController();
    const signal = this.recordingAbortController.signal;

    const progressCallback = (progress: ProgressInfo) => {
      this.state.videoGenerationProgress = progress;
      onProgress?.(progress);
    };

    const videoBlob = await this.canvasRecorder?.recordVideo(
      progressCallback,
      signal,
    );
    if (videoBlob && !signal.aborted) {
      this.state.recordedVideo = ref(videoBlob);
    }
    this.state.isRecording = false;
    this.recordingAbortController = undefined;
  };

  setDisabled = (disabled: boolean) => {
    if (!disabled) {
      this.state.isPlaying = false;
    }
    this.state.disabled = disabled;
  };

  resetVideoRecording = () => {
    this.recordingAbortController?.abort();
    this.recordingAbortController = undefined;
    this.state.isRecording = false;
    this.state.recordedVideo = undefined;
    this.state.videoGenerationProgress = undefined;
  };

  reset = (trimFrameStart?: number, trimFrameEnd?: number) => {
    setStoreValues(this.state, {
      ...defaultValues,
      trimFrameStart,
      trimFrameEnd,
    });
  };

  // TODO: Saving trimSettings shouldn't be part of the player store
  private saveTrimSettings = debounce(() => {
    const jobId = animationGenerationStore.state.jobData?.jobID;
    const params = new URLSearchParams(window.location.search);
    const animationNumber = Number(params.get('animationNumber')) || 0;

    const createdBy = animationGenerationStore.state.jobData?.createdBy;
    const currentUser = authStore.state.userID;
    if (createdBy !== currentUser) {
      console.warn(
        'Not saving trim settings because current user did not create the job',
      );
      return;
    }

    if (
      jobId &&
      this.state.trimFrameStart !== undefined &&
      this.state.trimFrameEnd !== undefined &&
      this.state.trimFrameStart < this.state.trimFrameEnd
    ) {
      postMotionSettings(
        jobId,
        animationNumber,
        this.state.trimFrameStart,
        this.state.trimFrameEnd,
      );
    }
  }, 1000);
}

// Create and export the factory function
export function getAnimationPlayerStore() {
  if (!AnimationPlayerStoreInstance.instances.has('default')) {
    AnimationPlayerStoreInstance.instances.set(
      'default',
      new AnimationPlayerStoreInstance(),
    );
  }
  return AnimationPlayerStoreInstance.instances.get('default')!;
}

// Add new factory method with caching
export function getAnimationPlayerStoreByID(id: string) {
  if (!AnimationPlayerStoreInstance.instances.has(id)) {
    AnimationPlayerStoreInstance.instances.set(
      id,
      new AnimationPlayerStoreInstance(),
    );
  }
  const instance = AnimationPlayerStoreInstance.instances.get(id);
  if (!instance) {
    throw new Error(`Instance with id ${id} not found`);
  }
  return instance;
}

export function removeAnimationPlayerStoreByID(id: string) {
  AnimationPlayerStoreInstance.instances.delete(id);
}

// Create the default instance
export const defaultInstance = getAnimationPlayerStore();
export const state = defaultInstance.state;

// Export all methods from the default instance
export const {
  frameJump,
  setFrame,
  playOrPause,
  setIsPlaying,
  setCameraControl,
  hoistAnimationMixer,
  setCurrentFrame,
  setDuration,
  zoomIn,
  zoomOut,
  setControls,
  setMixerReady,
  updateCamera,
  setTrimFrames,
  setCameraInputMode,
  setCanvasRecorder,
  startVideoRecording,
  resetVideoRecording,
  reset,
} = defaultInstance;
